/**
 * InvestmentDeclaration.js
 *
 * Validations added:
 * 1. 80C: ≥0, ≤150000
 * 2. 80D: ≥0, ≤75000
 * 3. Annual Rent: ≥0
 * 4. Landlord PAN: required + format (AAAAA9999A) when rent > ₹1,00,000
 * 5. File uploads: only PDF/JPG/PNG, max 5MB each
 * 6. prompt() for rejection → replaced with RejectionModal
 * 7. inline errors shown below each field after submission attempt
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Form, Button, Spinner,
    Alert, Badge, Table, Modal,
} from 'react-bootstrap';
import {
    FileText, Upload, CheckCircle, Clock,
    XCircle, Info, IndianRupee,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const ADMIN_ROLES  = ['admin', 'super-admin', 'hr-manager', 'account-manager'];
const MAX_FILE_MB  = 5;
const PAN_REGEX    = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const currentFY = () => {
    const d = new Date();
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String(y + 1).slice(2)}`;
};

const StatusBadge = ({ status }) => {
    const map = {
        pending:  ['warning', '⏳ Pending Review'],
        approved: ['success', '✓ Approved'],
        rejected: ['danger',  '✗ Rejected'],
    };
    const [bg, label] = map[status] || ['secondary', status];
    return <Badge bg={bg} text={bg === 'warning' ? 'dark' : undefined}>{label}</Badge>;
};

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-IN');

// ── Validation ────────────────────────────────────────────────────────────────
const validateDeclaration = (form, files) => {
    const e = {};

    const c80 = parseFloat(form.amount_80c);
    if (form.amount_80c !== '' && form.amount_80c !== null) {
        if (isNaN(c80) || c80 < 0)      e.amount_80c = 'Enter a valid amount (≥ ₹0)';
        else if (c80 > 150000)           e.amount_80c = 'Section 80C limit is ₹1,50,000';
    }

    const c80d = parseFloat(form.amount_80d);
    if (form.amount_80d !== '' && form.amount_80d !== null) {
        if (isNaN(c80d) || c80d < 0)    e.amount_80d = 'Enter a valid amount (≥ ₹0)';
        else if (c80d > 75000)           e.amount_80d = 'Section 80D limit is ₹75,000';
    }

    const rent = parseFloat(form.annual_rent);
    if (form.annual_rent !== '' && form.annual_rent !== null) {
        if (isNaN(rent) || rent < 0)     e.annual_rent = 'Enter a valid rent amount (≥ ₹0)';
        else if (rent > 100000) {
            // PAN required if rent > 1 lakh
            const pan = form.landlord_pan?.trim().toUpperCase();
            if (!pan)                    e.landlord_pan = 'Landlord PAN is required when annual rent exceeds ₹1,00,000';
            else if (!PAN_REGEX.test(pan)) e.landlord_pan = 'Invalid PAN format (e.g. ABCDE1234F)';
        }
    }

    // Landlord PAN format check even when entered independently
    if (form.landlord_pan?.trim() && !e.landlord_pan) {
        const pan = form.landlord_pan.trim().toUpperCase();
        if (!PAN_REGEX.test(pan))        e.landlord_pan = 'Invalid PAN format (e.g. ABCDE1234F)';
    }

    // File size checks
    const checkFile = (key, label) => {
        if (files[key] && files[key].size > MAX_FILE_MB * 1024 * 1024)
            e[key] = `${label} must be smaller than ${MAX_FILE_MB}MB`;
    };
    checkFile('proof_80c', '80C proof');
    checkFile('proof_80d', '80D proof');
    checkFile('proof_hra', 'HRA receipts');

    return e;
};

// ── Rejection Modal (replaces prompt()) ──────────────────────────────────────
const RejectionModal = ({ show, onConfirm, onCancel }) => {
    const [reason,  setReason]  = useState('');
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        await onConfirm(reason);
        setLoading(false);
        setReason('');
    };

    return (
        <Modal show={show} onHide={onCancel} centered size="sm">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Reject Declaration</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Label className="small fw-bold">
                    Rejection Reason
                    <span className="text-muted fw-normal ms-1">(optional)</span>
                </Form.Label>
                <Form.Control
                    as="textarea" rows={3}
                    maxLength={500}
                    placeholder="Let the employee know why their declaration was rejected…"
                    value={reason}
                    onChange={e => setReason(e.target.value)}/>
                {reason.length > 400 && (
                    <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        {reason.length}/500
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 justify-content-center gap-2">
                <Button variant="light" onClick={onCancel} disabled={loading}>Cancel</Button>
                <Button variant="danger" onClick={handleConfirm} disabled={loading}>
                    {loading ? <Spinner size="sm"/> : '✗ Reject Declaration'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ── Inline field error ────────────────────────────────────────────────────────
const FieldError = ({ msg }) => msg
    ? <div className="text-danger mt-1" style={{ fontSize: 12.5 }}>{msg}</div>
    : null;

// ════════════════════════════════════════════════════════════════════════════
const InvestmentDeclaration = () => {
    const { user } = useAuth();
    const isHR     = ADMIN_ROLES.includes(user?.role);
    const fy       = currentFY();

    const [decl,     setDecl]     = useState(null);
    const [form,     setForm]     = useState({
        amount_80c: '', amount_80d: '', annual_rent: '', landlord_pan: '',
    });
    const [files,    setFiles]    = useState({});
    const [errors,   setErrors]   = useState({});
    const [attempted,setAttempted]= useState(false);
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);

    // HR state
    const [queue,    setQueue]       = useState([]);
    const [qLoading, setQLoading]    = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [tab,      setTab]         = useState('declaration');
    const [rejection, setRejection]  = useState({ show: false, id: null });

    const loadMyDecl = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/payroll/investments/my');
            const d   = res.data.data;
            if (d) {
                setDecl(d);
                setForm({
                    amount_80c:   String(d.amount_80c   || ''),
                    amount_80d:   String(d.amount_80d   || ''),
                    annual_rent:  String(d.annual_rent  || ''),
                    landlord_pan: d.landlord_pan || '',
                });
            }
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };

    const loadQueue = async (status = statusFilter) => {
        setQLoading(true);
        try {
            const params = {};
            if (status) params.status = status;
            const res = await axiosInstance.get('/payroll/investments/review-queue', { params });
            setQueue(res.data.data || []);
        } catch { toast.error('Failed to load review queue'); }
        finally { setQLoading(false); }
    };

    useEffect(() => {
        loadMyDecl();
        if (isHR) loadQueue('');
    }, [isHR]);

    const set = (k, v) => {
        setForm(p => ({ ...p, [k]: v }));
        if (attempted) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const setFile = (k, file) => {
        if (!file) return;
        const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(file.type)) {
            toast.error('Only PDF, JPG, and PNG files are accepted');
            return;
        }
        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            toast.error(`File must be smaller than ${MAX_FILE_MB}MB`);
            return;
        }
        setFiles(p => ({ ...p, [k]: file }));
        if (attempted) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAttempted(true);
        const errs = validateDeclaration(form, files);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('financial_year', fy);
            fd.append('amount_80c',  form.amount_80c  || 0);
            fd.append('amount_80d',  form.amount_80d  || 0);
            fd.append('annual_rent', form.annual_rent || 0);
            if (form.landlord_pan)  fd.append('landlord_pan', form.landlord_pan.trim().toUpperCase());
            if (files.proof_80c)    fd.append('proof_80c',  files.proof_80c);
            if (files.proof_80d)    fd.append('proof_80d',  files.proof_80d);
            if (files.proof_hra)    fd.append('proof_hra',  files.proof_hra);
            await axiosInstance.post('/payroll/investments/declare', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Investment declaration submitted successfully');
            setAttempted(false);
            setErrors({});
            loadMyDecl();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Submission failed');
        } finally { setSaving(false); }
    };

    // Approve
    const handleApprove = async (id) => {
        try {
            await axiosInstance.post('/payroll/investments/approve', {
                declaration_id: id, status: 'approved', admin_remarks: '',
            });
            toast.success('Declaration approved');
            loadQueue(statusFilter);
        } catch { toast.error('Action failed'); }
    };

    // Reject — opens modal instead of prompt()
    const handleRejectConfirm = async (reason) => {
        try {
            await axiosInstance.post('/payroll/investments/approve', {
                declaration_id: rejection.id,
                status: 'rejected',
                admin_remarks: reason || '',
            });
            toast.success('Declaration rejected');
            setRejection({ show: false, id: null });
            loadQueue(statusFilter);
        } catch { toast.error('Action failed'); }
    };

    const handleStatusFilterChange = (val) => {
        setStatusFilter(val);
        loadQueue(val);
    };

    if (loading) return (
        <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
    );

    return (
        <div className="p-2 p-md-4">
            <div className="mb-4">
                <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                    <FileText size={22} className="text-primary"/>Investment Declaration
                </h4>
                <p className="text-muted small mb-0">
                    Financial Year: <strong>{fy}</strong> &nbsp;·&nbsp;
                    Declare investments to reduce TDS from salary
                </p>
            </div>

            {isHR && (
                <div className="d-flex gap-2 mb-4">
                    {['declaration', 'review'].map(t => (
                        <Button key={t} size="sm"
                            variant={tab === t ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab(t)}>
                            {t === 'declaration' ? 'My Declaration' : `Review Queue (${queue.length})`}
                        </Button>
                    ))}
                </div>
            )}

            {/* ── My Declaration Tab ────────────────────────────────── */}
            {tab === 'declaration' && (
                <>
                    {decl && (
                        <Alert
                            variant={decl.status === 'approved' ? 'success'
                                   : decl.status === 'rejected' ? 'danger' : 'info'}
                            className="d-flex align-items-center gap-3 mb-4">
                            {decl.status === 'approved' ? <CheckCircle size={18}/>
                             : decl.status === 'rejected' ? <XCircle size={18}/>
                             : <Clock size={18}/>}
                            <div>
                                <div className="fw-bold">Status: <StatusBadge status={decl.status}/></div>
                                {decl.admin_remarks && (
                                    <div className="small mt-1">HR Note: {decl.admin_remarks}</div>
                                )}
                            </div>
                        </Alert>
                    )}

                    <Alert variant="light" className="border small mb-4">
                        <Info size={14} className="me-2 text-primary"/>
                        Declare investments by <strong>31st January</strong> for TDS adjustment.
                        You can update until HR approves. Proofs required for final approval.
                        Accepted file types: PDF, JPG, PNG (max {MAX_FILE_MB}MB each).
                    </Alert>

                    <Form onSubmit={handleSubmit} noValidate>
                        <Row className="g-4">
                            {/* 80C */}
                            <Col md={6}>
                                <Card className="border-0 shadow-sm p-4 h-100">
                                    <div className="fw-bold mb-3 d-flex align-items-center gap-2">
                                        <IndianRupee size={16} className="text-primary"/>
                                        Section 80C — Max ₹1,50,000
                                    </div>
                                    <p className="text-muted small mb-3">
                                        LIC, PPF, ELSS, NSC, home loan principal, tuition fees.
                                    </p>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">Declared Amount (₹)</Form.Label>
                                        <Form.Control
                                            type="number" min="0" max="150000" step="100"
                                            placeholder="e.g. 120000"
                                            value={form.amount_80c}
                                            isInvalid={attempted && !!errors.amount_80c}
                                            onChange={e => set('amount_80c', e.target.value)}
                                            disabled={decl?.status === 'approved'}/>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.amount_80c}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">
                                            Proof (PDF/JPG/PNG, max {MAX_FILE_MB}MB)
                                        </Form.Label>
                                        {decl?.proof_80c && !files.proof_80c && (
                                            <div className="small text-success mb-1">✓ Proof uploaded</div>
                                        )}
                                        <Form.Control
                                            type="file" accept=".pdf,.jpg,.jpeg,.png"
                                            isInvalid={attempted && !!errors.proof_80c}
                                            onChange={e => setFile('proof_80c', e.target.files[0])}
                                            disabled={decl?.status === 'approved'}/>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.proof_80c}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Card>
                            </Col>

                            {/* 80D */}
                            <Col md={6}>
                                <Card className="border-0 shadow-sm p-4 h-100">
                                    <div className="fw-bold mb-3 d-flex align-items-center gap-2">
                                        <IndianRupee size={16} className="text-primary"/>
                                        Section 80D — Max ₹25,000
                                    </div>
                                    <p className="text-muted small mb-3">
                                        Health insurance for self, spouse, children, parents.
                                    </p>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">Declared Amount (₹)</Form.Label>
                                        <Form.Control
                                            type="number" min="0" max="75000" step="100"
                                            placeholder="e.g. 20000"
                                            value={form.amount_80d}
                                            isInvalid={attempted && !!errors.amount_80d}
                                            onChange={e => set('amount_80d', e.target.value)}
                                            disabled={decl?.status === 'approved'}/>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.amount_80d}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">
                                            Proof (PDF/JPG/PNG, max {MAX_FILE_MB}MB)
                                        </Form.Label>
                                        {decl?.proof_80d && !files.proof_80d && (
                                            <div className="small text-success mb-1">✓ Proof uploaded</div>
                                        )}
                                        <Form.Control
                                            type="file" accept=".pdf,.jpg,.jpeg,.png"
                                            isInvalid={attempted && !!errors.proof_80d}
                                            onChange={e => setFile('proof_80d', e.target.files[0])}
                                            disabled={decl?.status === 'approved'}/>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.proof_80d}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Card>
                            </Col>

                            {/* HRA */}
                            <Col xs={12}>
                                <Card className="border-0 shadow-sm p-4">
                                    <div className="fw-bold mb-3 d-flex align-items-center gap-2">
                                        <IndianRupee size={16} className="text-primary"/>
                                        HRA — House Rent Allowance
                                    </div>
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">Annual Rent Paid (₹)</Form.Label>
                                                <Form.Control
                                                    type="number" min="0" step="1000"
                                                    placeholder="e.g. 180000"
                                                    value={form.annual_rent}
                                                    isInvalid={attempted && !!errors.annual_rent}
                                                    onChange={e => set('annual_rent', e.target.value)}
                                                    disabled={decl?.status === 'approved'}/>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.annual_rent}
                                                </Form.Control.Feedback>
                                                <Form.Text className="text-muted small">
                                                    PAN required if annual rent &gt; ₹1,00,000
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">
                                                    Landlord PAN
                                                    {parseFloat(form.annual_rent) > 100000 && (
                                                        <span className="text-danger ms-1">*</span>
                                                    )}
                                                </Form.Label>
                                                <Form.Control
                                                    maxLength={10}
                                                    placeholder="e.g. ABCDE1234F"
                                                    value={form.landlord_pan}
                                                    isInvalid={attempted && !!errors.landlord_pan}
                                                    onChange={e => set('landlord_pan', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                                    disabled={decl?.status === 'approved'}/>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.landlord_pan}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">
                                                    Rent Receipts (PDF/JPG/PNG, max {MAX_FILE_MB}MB)
                                                </Form.Label>
                                                {decl?.proof_hra && !files.proof_hra && (
                                                    <div className="small text-success mb-1">✓ Receipts uploaded</div>
                                                )}
                                                <Form.Control
                                                    type="file" accept=".pdf,.jpg,.jpeg,.png"
                                                    isInvalid={attempted && !!errors.proof_hra}
                                                    onChange={e => setFile('proof_hra', e.target.files[0])}
                                                    disabled={decl?.status === 'approved'}/>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.proof_hra}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>

                        {decl?.status !== 'approved' && (
                            <div className="mt-4">
                                <Button variant="primary" type="submit" disabled={saving}>
                                    {saving
                                        ? <><Spinner size="sm" className="me-1"/>Submitting…</>
                                        : <><Upload size={14} className="me-1"/>
                                           {decl ? 'Update Declaration' : 'Submit Declaration'}</>}
                                </Button>
                            </div>
                        )}
                    </Form>
                </>
            )}

            {/* ── HR Review Queue Tab ───────────────────────────────── */}
            {tab === 'review' && isHR && (
                <>
                    <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                        <div>
                            <Form.Label className="small fw-bold mb-1">Filter by Status</Form.Label>
                            <Form.Select size="sm" style={{ width: 180 }}
                                value={statusFilter}
                                onChange={e => handleStatusFilterChange(e.target.value)}>
                                <option value="">All Declarations</option>
                                <option value="pending">Pending Review</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </Form.Select>
                        </div>
                        <div className="text-muted small mt-auto">
                            {queue.length} record{queue.length !== 1 ? 's' : ''}
                        </div>
                    </div>

                    {qLoading ? (
                        <div className="text-center py-4"><Spinner animation="border"/></div>
                    ) : queue.length === 0 ? (
                        <Alert variant="light" className="border text-center py-5">
                            <CheckCircle size={32} className="text-success mb-2 d-block mx-auto"/>
                            <div className="text-muted">No declarations found.</div>
                        </Alert>
                    ) : (
                        <Card className="border-0 shadow-sm">
                            <Table hover responsive className="align-middle small mb-0">
                                <thead className="bg-light">
                                    <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                        <th className="ps-3">Employee</th>
                                        <th>FY</th>
                                        <th className="text-end">80C</th>
                                        <th className="text-end">80D</th>
                                        <th className="text-end">HRA Rent</th>
                                        <th>Proofs</th>
                                        <th>Status</th>
                                        <th className="pe-3 text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {queue.map(d => (
                                        <tr key={d.id}>
                                            <td className="ps-3">
                                                <div className="fw-medium">{d.employee_name}</div>
                                                <div className="text-muted" style={{ fontSize: 10 }}>
                                                    {d.employee_code}
                                                </div>
                                            </td>
                                            <td>{d.financial_year}</td>
                                            <td className="text-end">₹{fmt(d.amount_80c)}</td>
                                            <td className="text-end">₹{fmt(d.amount_80d)}</td>
                                            <td className="text-end">₹{fmt(d.annual_rent)}</td>
                                            <td>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {d.proof_80c && <Badge bg="light" text="dark" style={{ fontSize: 9 }}>80C ✓</Badge>}
                                                    {d.proof_80d && <Badge bg="light" text="dark" style={{ fontSize: 9 }}>80D ✓</Badge>}
                                                    {d.proof_hra && <Badge bg="light" text="dark" style={{ fontSize: 9 }}>HRA ✓</Badge>}
                                                    {!d.proof_80c && !d.proof_80d && !d.proof_hra && (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <StatusBadge status={d.status}/>
                                                {d.admin_remarks && (
                                                    <div className="text-muted small mt-1" style={{ fontSize: 10, maxWidth: 120 }}>
                                                        {d.admin_remarks}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="pe-3 text-end">
                                                {d.status === 'pending' ? (
                                                    <div className="d-flex gap-1 justify-content-end">
                                                        <Button size="sm" variant="outline-success" className="py-0"
                                                            onClick={() => handleApprove(d.id)}>
                                                            <CheckCircle size={12} className="me-1"/>Approve
                                                        </Button>
                                                        <Button size="sm" variant="outline-danger" className="py-0"
                                                            onClick={() => setRejection({ show: true, id: d.id })}>
                                                            <XCircle size={12} className="me-1"/>Reject
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small">
                                                        {d.status === 'approved' ? '✓ Done' : '✗ Done'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                </>
            )}

            {/* Rejection Modal */}
            <RejectionModal
                show={rejection.show}
                onConfirm={handleRejectConfirm}
                onCancel={() => setRejection({ show: false, id: null })}/>
        </div>
    );
};

export default InvestmentDeclaration;
